/**Usage Example**:
 * ```tsx
 * const saveButtonRef = useRef<SaveButtonRef>(null);
 *
 * const handleSuccess = () => {
 *   saveButtonRef.current?.onSuccess();
 * };
 *
 * return <SaveButton ref={saveButtonRef} isPending={isPending} />;
 * ```
 */

import { CheckIcon } from "@radix-ui/react-icons";
import { Loader } from "lucide-react";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { Button } from "~/components/ui/button";
import { trackEvent } from "~/utils/analytics";

const messages = defineMessages({
  save: { id: "save", defaultMessage: "Save" },
  saving: { id: "saving", defaultMessage: "Saving..." },
  saved: { id: "saved", defaultMessage: "Saved" },
});

type SaveButtonProps = {
  isPending: boolean;
};

export type SaveButtonRef = {
  onSuccess: () => void;
};

const TIMEOUT = 2000;

export const SaveButton = forwardRef<SaveButtonRef, SaveButtonProps>(
  ({ isPending }, ref) => {
    const intl = useIntl();
    const [isSuccessfullySaved, setIsSuccessfullySaved] = useState(false);
    const [btnText, setBtnText] = useState<keyof typeof messages>("save");

    useEffect(() => {
      if (isPending) {
        setBtnText("saving");
      } else if (isSuccessfullySaved) {
        setBtnText("saved");
      } else {
        setBtnText("save");
      }
    }, [isPending, isSuccessfullySaved]);

    useEffect(() => {
      if (isSuccessfullySaved) {
        const timer = setTimeout(() => {
          setIsSuccessfullySaved(false);
        }, TIMEOUT);

        return () => clearTimeout(timer);
      }
    }, [isSuccessfullySaved, TIMEOUT]);

    useImperativeHandle(ref, () => ({
      onSuccess() {
        setIsSuccessfullySaved(true);
      },
    }));

    return (
      <Button
        disabled={isPending || isSuccessfullySaved}
        className={`mt-4 self-start py-2 px-8 ${
          isSuccessfullySaved
            ? "bg-green-600 hover:bg-green-700"
            : "bg-blue-600 hover:bg-blue-700"
        }`}
        onClick={() => {
          trackEvent({
            eventName: "app/settings/save.button/click",
          });
        }}
        type="submit"
      >
        {isPending && <Loader className="mr-2 h-4 w-4 animate-spin-slow" />}
        {isSuccessfullySaved && <CheckIcon className="mr-2 h-4 w-4" />}
        {intl.formatMessage(messages[btnText])}
      </Button>
    );
  }
);

SaveButton.displayName = "SaveButton";
